import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import Header from '../../components/header/Header';
import Hero from '../../components/hero/Hero';
import BrandingLayout from '../../components/branding-layout/BrandingLayout';
import { trackEvent } from '../../utils/analytics';
import './Landing.css';

const WhyChooseReffconnect = lazy(() => import('../../components/why-choose-connectX/WhyChooseConnectX'));
const HowItWorks = lazy(() => import('../../components/how-it-works/HowItWorks'));
const HowReffconnectWorks = lazy(() => import('../../components/how-connectx-works/HowConnectXWorks'));
const ServicesHighlight = lazy(() => import('../../components/servieces container/ServicesHighlight'));
const Companies = lazy(() => import('../../components/Companies/Companies'));
const FAQ = lazy(() => import('../../components/FAQ/FAQ'));
const Footer = lazy(() => import('../../components/footer/Footer'));
const ContactForm = lazy(() => import('../../components/contact us/contact form/ContactForm'));

function SectionFallback({ label }: { label?: string }) {
  return (
    <div
      className="landing-lazy-fallback"
      role="status"
      aria-live="polite"
      aria-label={label ? `Loading ${label}` : 'Loading section'}
    />
  );
}

/** Mount children only when near viewport, then load lazy chunk */
function LazyMount({
  minHeight,
  children,
  paintAlways,
}: {
  minHeight?: string | number;
  children: React.ReactNode;
  /** Avoid content-visibility skipping (fixes missing text in some nested cards). */
  paintAlways?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const ob = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          ob.disconnect();
        }
      },
      { root: null, rootMargin: '180px 0px', threshold: 0 }
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={paintAlways ? 'landing-lazy-mount landing-lazy-mount--paint-always' : 'landing-lazy-mount'}
      style={visible ? undefined : { minHeight: minHeight ?? '28vh' }}
    >
      {visible ? children : null}
    </div>
  );
}

const LandingPage: React.FC = () => {
  const landingRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = landingRootRef.current;
    if (!root) return;

    const getSectionName = (element: Element) =>
      element.closest<HTMLElement>('[data-analytics-section]')?.dataset.analyticsSection || 'landing';

    const onClick = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const clickable = target.closest<HTMLElement>('button, a, [role="button"], input[type="button"], input[type="submit"]');
      if (!clickable || !root.contains(clickable)) return;

      const rawLabel =
        clickable.getAttribute('aria-label') ||
        clickable.getAttribute('title') ||
        clickable.textContent ||
        (clickable as HTMLInputElement).value ||
        clickable.id ||
        clickable.className ||
        '';
      const label = rawLabel.replace(/\s+/g, ' ').trim().slice(0, 80) || 'unknown';
      const href = clickable instanceof HTMLAnchorElement ? clickable.getAttribute('href') || '' : '';

      trackEvent('landing_click', {
        section: getSectionName(clickable),
        element_tag: clickable.tagName.toLowerCase(),
        label,
        href,
      });
    };

    const onSubmit = (event: Event) => {
      const form = event.target as HTMLFormElement | null;
      if (!form || !root.contains(form)) return;

      trackEvent('landing_form_submit', {
        section: getSectionName(form),
        form_id: form.id || 'no_id',
      });
    };

    root.addEventListener('click', onClick, true);
    root.addEventListener('submit', onSubmit, true);

    return () => {
      root.removeEventListener('click', onClick, true);
      root.removeEventListener('submit', onSubmit, true);
    };
  }, []);

  return (
    <div
      ref={landingRootRef}
      className="landing-page"
      style={{
        minHeight: '100vh',
        background: 'var(--color-bg-main)',
        color: 'var(--color-text-main)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="landing-page-foreground">
      <Header showLandingNav />
      <div className="landing-main" role="main" id="main-content">
        <div data-analytics-section="hero">
          <Hero />
        </div>

        <LazyMount minHeight="min(420px, 45vh)">
          <div data-analytics-section="branding">
            <BrandingLayout />
          </div>
        </LazyMount>

        <LazyMount minHeight="min(260px, 30vh)" paintAlways>
          <Suspense fallback={<SectionFallback label="services" />}>
            <div data-analytics-section="services-highlight">
              <ServicesHighlight />
            </div>
          </Suspense>
        </LazyMount>

        <LazyMount minHeight="min(400px, 45vh)">
          <Suspense fallback={<SectionFallback label="how it works" />}>
            <div data-analytics-section="how-it-works">
              <HowItWorks />
            </div>
          </Suspense>
        </LazyMount>

        <LazyMount minHeight="min(480px, 50vh)">
          <Suspense fallback={<SectionFallback label="features" />}>
            <div data-analytics-section="why-choose">
              <WhyChooseReffconnect />
            </div>
          </Suspense>
        </LazyMount>

        <LazyMount minHeight="min(520px, 55vh)">
          <Suspense fallback={<SectionFallback label="workflow" />}>
            <div data-analytics-section="workflow">
              <HowReffconnectWorks />
            </div>
          </Suspense>
        </LazyMount>

        <LazyMount minHeight="min(320px, 35vh)">
          <Suspense fallback={<SectionFallback label="companies" />}>
            <div data-analytics-section="companies">
              <Companies />
            </div>
          </Suspense>
        </LazyMount>

        <LazyMount minHeight="min(360px, 40vh)">
          <Suspense fallback={<SectionFallback label="FAQ" />}>
            <div data-analytics-section="faq">
              <FAQ />
            </div>
          </Suspense>
        </LazyMount>

        <LazyMount minHeight="min(620px, 60vh)">
          <Suspense fallback={<SectionFallback label="contact form" />}>
            <div data-analytics-section="contact">
              <ContactForm />
            </div>
          </Suspense>
        </LazyMount>

        <LazyMount minHeight="min(280px, 32vh)">
          <Suspense fallback={<SectionFallback label="footer" />}>
            <div data-analytics-section="footer">
              <Footer />
            </div>
          </Suspense>
        </LazyMount>
      </div>
      </div>
    </div>
  );
};

export default LandingPage;

